import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Hand, OctagonAlert } from "lucide-react";

import { Tasks } from "@/api/resources";
import type { TaskWorkLogEntryCreate, TaskWorkLogEntryOut } from "@/api/types";
import { ToolSlugChip } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { StaleCard, WarningCard } from "@/components/ui/Card";
import { nameOf, usePeople } from "@/lib/people";
import { Markdown } from "@/lib/markdown";

interface TaskJournalProps {
  taskId: string;
  /**
   * Spec 021: on the task screen the entries live in the feed with the
   * messages (`JournalCard`), so this panel is only the form.
   */
  showEntries?: boolean;
}

// d5b limits — kept in sync with the API schema (single source there).
const MAX_SUMMARY = 200;
const MAX_APPROACH = 1000;
const MAX_OUTCOME = 500;
const MAX_PROGRESS = 300; // Spec 013 §7
const EMPTY_PROGRESS = {
  current_stage: "",
  next_step: "",
  blocker: "",
  needs: "",
  needs_user_id: "",
};

/**
 * Spec 006 US-5 + d5b: append-only structured work journal panel.
 *
 * Each entry records a *decision*, not a transcript: a one-line summary,
 * the approach taken (and why), the tools used, and the outcome. Renders
 * existing entries oldest-first and offers a structured append form. The
 * journal is append-only — there is no edit or delete on entries (FR-016).
 *
 * Legacy entries (written before the structured model) have null structured
 * fields and a populated `body_md`; they fall back to rendering `body_md`.
 */
export function TaskJournal({ taskId, showEntries = true }: TaskJournalProps) {
  const qc = useQueryClient();
  const entries = useQuery({
    queryKey: ["task-journal", taskId],
    queryFn: () => Tasks.listJournal(taskId),
    enabled: Boolean(taskId) && showEntries,
  });

  const [summary, setSummary] = useState("");
  const [approach, setApproach] = useState("");
  const [outcome, setOutcome] = useState("");
  const [draftTools, setDraftTools] = useState("");
  const [progress, setProgress] = useState(EMPTY_PROGRESS);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const people = usePeople();

  const append = useMutation({
    mutationFn: (body: TaskWorkLogEntryCreate) => Tasks.appendJournalEntry(taskId, body),
    onSuccess: () => {
      setSummary("");
      setApproach("");
      setOutcome("");
      setDraftTools("");
      setProgress(EMPTY_PROGRESS);
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ["task-journal", taskId] });
    },
    onError: (err: unknown) => {
      const m = err instanceof Error ? err.message : "Не удалось сохранить запись";
      setErrorMsg(m);
    },
  });

  // Spec 013 §7: a request names both the person and what they must do.
  const needsIncomplete = Boolean(progress.needs.trim()) !== Boolean(progress.needs_user_id);
  const canSubmit =
    summary.trim().length > 0 &&
    approach.trim().length > 0 &&
    outcome.trim().length > 0 &&
    !needsIncomplete &&
    !append.isPending;

  const submit = () => {
    const s = summary.trim();
    const a = approach.trim();
    const o = outcome.trim();
    if (!s || !a || !o || needsIncomplete) return;
    const tool_slugs = draftTools
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const body: TaskWorkLogEntryCreate = { summary: s, approach: a, outcome: o };
    if (tool_slugs.length > 0) body.tool_slugs = tool_slugs;
    for (const key of ["current_stage", "next_step", "blocker", "needs"] as const) {
      const value = progress[key].trim();
      if (value) body[key] = value;
    }
    if (progress.needs_user_id && body.needs) body.needs_user_id = progress.needs_user_id;
    append.mutate(body);
  };

  return (
    <section className="space-y-2 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <div className="text-micro font-semibold uppercase tracking-[.08em] text-p-text-muted">
        {showEntries ? "Журнал работы" : "Записать в журнал"}
      </div>

      {!showEntries ? null : entries.isLoading ? (
        <p className="text-sm text-hint">Загружаю…</p>
      ) : entries.data && entries.data.length > 0 ? (
        <ul className="space-y-2">
          {entries.data.map((entry) => (
            <JournalEntry key={entry.id} entry={entry} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-hint italic">
          Журнал пуст. Запиши, как решали — не что нажимал, а почему так.
        </p>
      )}

      <div className="space-y-2 pt-1">
        <JournalField
          id="journal-summary"
          label="Что сделано"
          hint="одна фраза"
          value={summary}
          onChange={setSummary}
          max={MAX_SUMMARY}
          disabled={append.isPending}
          onSubmit={submit}
        />
        <JournalField
          id="journal-approach"
          label="Подход"
          hint="метод и почему так решили"
          value={approach}
          onChange={setApproach}
          max={MAX_APPROACH}
          multiline
          disabled={append.isPending}
          onSubmit={submit}
        />
        <JournalField
          id="journal-outcome"
          label="Результат"
          hint="сработало / нет / с оговоркой"
          value={outcome}
          onChange={setOutcome}
          max={MAX_OUTCOME}
          multiline
          disabled={append.isPending}
          onSubmit={submit}
        />
        <div className="space-y-1">
          <label htmlFor="journal-tools" className="block text-xs text-hint">
            Инструменты <span className="opacity-70">(через запятую, необязательно)</span>
          </label>
          <input
            id="journal-tools"
            type="text"
            className="block w-full min-h-touch rounded-card border border-hint/30 bg-sbg px-2 py-1 text-base focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="whisper, prosto-task"
            value={draftTools}
            onChange={(e) => setDraftTools(e.target.value)}
            disabled={append.isPending}
          />
        </div>
        <details className="rounded-card border border-sep px-2 py-1">
          <summary className="flex min-h-touch cursor-pointer items-center text-sm text-text">
            Этап, следующий шаг и чьё решение нужно — необязательно
          </summary>
          <p className="pb-2 text-xs text-hint">
            Партнёр увидит это в уведомлении и в «Что происходит».
          </p>
          <div className="space-y-2 pb-2">
            <JournalField
              id="journal-stage"
              label="Сейчас"
              hint="где работа стоит сейчас"
              value={progress.current_stage}
              onChange={(v) => setProgress((p) => ({ ...p, current_stage: v }))}
              max={MAX_PROGRESS}
              disabled={append.isPending}
              onSubmit={submit}
            />
            <JournalField
              id="journal-next"
              label="Дальше"
              hint="следующий шаг и кто его делает"
              value={progress.next_step}
              onChange={(v) => setProgress((p) => ({ ...p, next_step: v }))}
              max={MAX_PROGRESS}
              disabled={append.isPending}
              onSubmit={submit}
            />
            <JournalField
              id="journal-blocker"
              label="Препятствие"
              hint="что мешает прямо сейчас"
              value={progress.blocker}
              onChange={(v) => setProgress((p) => ({ ...p, blocker: v }))}
              max={MAX_PROGRESS}
              disabled={append.isPending}
              onSubmit={submit}
            />
            <div className="space-y-1">
              <label htmlFor="journal-needs-user" className="block text-xs text-hint">
                Чьё решение или действие нужно
              </label>
              <select
                id="journal-needs-user"
                className="block w-full min-h-touch rounded-card border border-sep bg-sbg px-2 py-1 text-base focus:outline-none focus:ring-2 focus:ring-accent"
                value={progress.needs_user_id}
                onChange={(e) => setProgress((p) => ({ ...p, needs_user_id: e.target.value }))}
                disabled={append.isPending}
              >
                <option value="">Никого не жду</option>
                {(people.data ?? []).map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
              </select>
            </div>
            <JournalField
              id="journal-needs"
              label="Что нужно"
              hint="решение или действие"
              value={progress.needs}
              onChange={(v) => setProgress((p) => ({ ...p, needs: v }))}
              max={MAX_PROGRESS}
              disabled={append.isPending}
              onSubmit={submit}
            />
            {needsIncomplete && (
              <p className="text-chip text-dstr" role="alert">
                Укажи и человека, и что от него нужно — или оставь оба поля пустыми.
              </p>
            )}
          </div>
        </details>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-hint">
            Ctrl/Cmd+Enter — добавить. Журнал только дополняется, редактировать нельзя.
          </span>
          <Button size="compact" onClick={submit} disabled={!canSubmit}
            loading={append.isPending} loadingText="Сохраняю…">
            Добавить
          </Button>
        </div>
        {errorMsg && (
          <p className="text-chip text-dstr" role="alert">
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
}

interface JournalFieldProps {
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  multiline?: boolean;
  disabled?: boolean;
  onSubmit: () => void;
}

function JournalField({
  id,
  label,
  hint,
  value,
  onChange,
  max,
  multiline = false,
  disabled = false,
  onSubmit,
}: JournalFieldProps) {
  const over = value.length > max;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit();
    }
  };
  const shared =
    "block w-full min-h-touch rounded-card border bg-sbg px-2 py-1 text-base focus:outline-none focus:ring-2 focus:ring-accent " +
    (over ? "border-dstr" : "border-sep");
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="text-xs text-hint">
          {label} <span className="opacity-70">— {hint}</span>
        </label>
        <span className={"text-chip " + (over ? "text-dstr" : "text-hint")}>
          {value.length}/{max}
        </span>
      </div>
      {multiline ? (
        <textarea
          id={id}
          rows={2}
          className={shared}
          value={value}
          maxLength={max}
          aria-invalid={over}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      ) : (
        <input
          id={id}
          type="text"
          className={shared}
          value={value}
          maxLength={max}
          aria-invalid={over}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
        />
      )}
    </div>
  );
}

/**
 * One journal entry (design spec §8, addendum §2).
 *
 * Three renderings, and which one you get is the whole point:
 *
 *  - **erroneous** — `--dstr` hairline, `--dstr-soft` header, `triangle-alert`.
 *    The decision was wrong.
 *  - **superseded** (FEAT-025) — `--sep` hairline, `--sbg` header, `clock` in
 *    `--hint`. The decision was right and the ground has since moved. Softer on
 *    purpose: warning about it as if it were a mistake misrepresents it.
 *  - plain.
 *
 * An entry can carry both flags — they are independent claims — so the
 * erroneous wrapper goes outside and the stale header nests within it, rather
 * than one silently winning.
 *
 * In every case the body renders at full contrast, never dimmed or struck
 * through: the annotation sits *on top of* an entry that stays readable, which
 * is what "запись оставлена как есть" means.
 */
function JournalEntry({ entry }: { entry: TaskWorkLogEntryOut }) {
  const stale = entry.superseded_flag ? (
    <StaleCard reason={<StaleReason reasons={entry.superseded_reasons} />}>
      <JournalBody entry={entry} />
    </StaleCard>
  ) : (
    <JournalBody entry={entry} />
  );

  return (
    <li>
      {entry.error_flag ? (
        <WarningCard reason={<ErrorReason reasons={entry.error_reasons} />}>{stale}</WarningCard>
      ) : entry.superseded_flag ? (
        stale
      ) : (
        <div className="rounded-card border border-p-border bg-p-surface-2 px-4 py-3">
          <JournalBody entry={entry} />
        </div>
      )}
    </li>
  );
}

/** §8 — the exact wording the spec puts in the warning header. */
function ErrorReason({ reasons }: { reasons: string[] }) {
  return (
    <>
      <span className="font-medium">Помечено ошибочным.</span>{" "}
      {reasons.length > 0 && <>{reasons.join(" · ")}. </>}
      Запись оставлена как есть: её нельзя удалить или отредактировать.
    </>
  );
}

/** Addendum §2 — the same shape, one register quieter. */
function StaleReason({ reasons }: { reasons: string[] }) {
  return (
    <>
      <span className="font-medium">Устарело.</span>{" "}
      {reasons.length > 0 && <>{reasons.join(" · ")}. </>}
      Запись оставлена как есть.
    </>
  );
}

/**
 * A journal entry in the task's feed (Spec 021, the canvas's «Журнал · агент
 * Сани» card): who wrote it and when on top, then the entry. The error and
 * stale treatments stay exactly as in the panel.
 */
export function JournalCard({ entry }: { entry: TaskWorkLogEntryOut }) {
  const people = usePeople();
  const who = nameOf(people.data, entry.actor_id) ?? "участник";
  const at = new Date(entry.created_at);
  const head = (
    <div className="flex items-baseline justify-between gap-2 text-meta text-p-text-muted">
      <span className="font-semibold">Журнал · {who}</span>
      <time dateTime={entry.created_at}>{at.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</time>
    </div>
  );
  const body = (
    <div className="flex flex-col gap-1.5">
      {head}
      <JournalBody entry={entry} hideTime />
    </div>
  );
  if (entry.error_flag) return <WarningCard reason={<ErrorReason reasons={entry.error_reasons} />}>{body}</WarningCard>;
  if (entry.superseded_flag) return <StaleCard reason={<StaleReason reasons={entry.superseded_reasons} />}>{body}</StaleCard>;
  return <div className="rounded-card border border-p-border bg-p-surface-2 px-4 py-3">{body}</div>;
}

function JournalBody({ entry, hideTime = false }: { entry: TaskWorkLogEntryOut; hideTime?: boolean }) {
  const when = new Date(entry.created_at);
  const isStructured = entry.summary !== null;
  return (
    <div className="flex flex-col gap-1.5">
      {isStructured ? (
        <>
          <p className="text-cell-title font-medium text-text">{entry.summary}</p>
          {entry.approach && (
            <Markdown className="text-body text-text" text={entry.approach} />
          )}
          {entry.outcome && (
            <div className="text-body text-hint">
              <span className="text-hint">Результат:</span>
              <Markdown text={entry.outcome} />
            </div>
          )}
        </>
      ) : (
        <>
          {/* §8 — legacy import: label it, then show the text as-is. We do not
              invent an approach or an outcome that was never written. */}
          <span className="text-micro uppercase tracking-[.05em] text-hint">
            Запись без структуры · импорт из бота
          </span>
          <Markdown className="text-body text-text" text={entry.body_md} />
        </>
      )}
      <JournalProgress entry={entry} />
      {entry.tool_slugs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {entry.tool_slugs.map((slug) => (
            <ToolSlugChip key={slug} slug={slug} />
          ))}
        </div>
      )}
      {!hideTime && <div className="text-chip text-hint">{when.toLocaleString("ru-RU")}</div>}
    </div>
  );
}

/**
 * Spec 013 §7 — where the work stood when this entry was written and whose
 * move it was. Labels are text, not colour: the blocker and the request carry
 * an icon and a word, so the state survives greyscale and screen readers.
 */
function JournalProgress({ entry }: { entry: TaskWorkLogEntryOut }) {
  const people = usePeople();
  const asked = nameOf(people.data, entry.needs_user_id ?? null);
  if (!entry.current_stage && !entry.next_step && !entry.blocker && !entry.needs) return null;
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-body">
      {entry.current_stage && (
        <>
          <dt className="text-hint">Сейчас</dt>
          <dd className="text-text">{entry.current_stage}</dd>
        </>
      )}
      {entry.next_step && (
        <>
          <dt className="text-hint">Дальше</dt>
          <dd className="text-text">{entry.next_step}</dd>
        </>
      )}
      {entry.blocker && (
        <>
          <dt className="flex items-center gap-1 text-dstr">
            <OctagonAlert className="h-4 w-4" aria-hidden="true" />
            Препятствие
          </dt>
          <dd className="text-text">{entry.blocker}</dd>
        </>
      )}
      {entry.needs && (
        <>
          <dt className="flex items-center gap-1 text-p-needs">
            <Hand className="h-4 w-4" aria-hidden="true" />
            {asked ? `Нужно от: ${asked}` : "Нужно"}
          </dt>
          <dd className="text-text">{entry.needs}</dd>
        </>
      )}
    </dl>
  );
}
